import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Chip,
  IconButton,
  Button,
  Tooltip as MuiTooltip,
  LinearProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  MdPeople,
  MdOutlineShoppingBag,
  MdOutlineDesignServices,
  MdOutlineCategory,
  MdEventNote,
  MdArrowUpward,
  MdArrowDownward,
  MdRefresh,
  MdCheckCircle,
  MdPendingActions,
  MdCancel,
  MdTrendingUp,
  MdVerifiedUser,
  MdDoneAll,
  MdSchedule,
  MdAnalytics,
  MdAssignmentInd,
  MdLocalShipping,
  MdErrorOutline,
  MdClose,
  MdOutlineCalendarToday,
  MdLocationOn,
  MdLaunch,
  MdOutlinePhotoCamera,
} from "react-icons/md";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { getAllUsers } from "../apis/userApi";
import { getAllOrders } from "../apis/orderApi";
import { getAllCategories } from "../apis/categoryApi";
import { getAllservices } from "../apis/servicesApi";
import { getAllAppointments } from "../apis/appointmentApi";
import { getAllEmployees } from "../apis/employeeApi";
import { getMe } from "../apis/LoginApi";
import { brand } from "../theme";

// ── Data Extractors & Normalizers ─────────────────────────────────────────
const getDate = (item) =>
  item?.date || item?.appointment_date || item?.appointmentDate || item?.slotDate || item?.bookedAt || item?.createdAt;

const getStatus = (item) => {
  const s = item?.status || item?.orderStatus || item?.appointment_status;
  if (!s) return "pending";
  return String(s).toLowerCase();
};

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) {
    return path;
  }
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  if (cleanPath.startsWith("uploads/")) {
    return `https://skishore.in/api/${cleanPath}`;
  }
  if (!cleanPath.startsWith("api/")) {
    return `https://skishore.in/api/${cleanPath}`;
  }
  return `https://skishore.in/${cleanPath}`;
};

const getCategoryName = (service) =>
  (typeof service?.category === "object" ? service?.category?.name : service?.category) || service?.category_name || "Uncategorized";

const getCustomerName = (apt) =>
  apt?.customer_name || apt?.customerName || apt?.userName || apt?.user?.name || apt?.name || "Guest Customer";

const getServiceName = (apt) =>
  apt?.service_name || apt?.serviceName || apt?.service?.name || apt?.title || "Standard Service";

// ── Pure Helpers ──────────────────────────────────────────────────────────
function buildDailyTrend(items, days = 14) {
  const buckets = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    buckets.push({
      key: d.toDateString(),
      label: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      count: 0,
      completed: 0,
    });
  }

  items.forEach((item) => {
    const raw = getDate(item);
    if (!raw) return;
    const d = new Date(raw);
    if (isNaN(d)) return;
    const bucket = buckets.find((b) => b.key === d.toDateString());
    if (bucket) {
      bucket.count += 1;
      const st = getStatus(item);
      if (st === "completed" || st === "confirmed" || st === "delivered") {
        bucket.completed += 1;
      }
    }
  });

  return buckets;
}

function weekOverWeek(items) {
  const now = new Date();
  const cutoff1 = new Date(now);
  cutoff1.setDate(now.getDate() - 7);
  const cutoff2 = new Date(now);
  cutoff2.setDate(now.getDate() - 14);

  let current = 0;
  let previous = 0;
  let hasDates = false;

  items.forEach((item) => {
    const raw = getDate(item);
    if (!raw) return;
    const d = new Date(raw);
    if (isNaN(d)) return;
    hasDates = true;
    if (d >= cutoff1) current += 1;
    else if (d >= cutoff2) previous += 1;
  });

  if (!hasDates) return null;
  const pct = previous === 0 ? (current > 0 ? 100 : 0) : Math.round(((current - previous) / previous) * 100);
  return { current, previous, pct };
}

function buildBreakdown(items, getter) {
  const map = new Map();
  let unresolved = 0;

  items.forEach((item) => {
    const key = getter(item);
    if (!key) {
      unresolved += 1;
      return;
    }
    map.set(key, (map.get(key) || 0) + 1);
  });

  const data = Array.from(map, ([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  return { data, unresolved, total: items.length };
}

const PIE_COLORS = [
  brand.brass,
  brand.ink950,
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EC4899",
  "#8B5CF6",
  "#64748B",
];

// ── UI Components ─────────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: brand.ink950,
        color: brand.parchment,
        px: 2,
        py: 1.25,
        borderRadius: "12px",
        boxShadow: "0 20px 35px -10px rgba(11,10,61,0.6)",
        border: `1px solid ${brand.brass}44`,
      }}
    >
      {label && <Typography sx={{ fontSize: "0.72rem", opacity: 0.7, mb: 0.5, fontWeight: 600 }}>{label}</Typography>}
      {payload.map((p, i) => (
        <Typography key={i} sx={{ fontWeight: 700, fontSize: "0.82rem", color: p.color || brand.brassLight }}>
          {p.name}: {p.value}
        </Typography>
      ))}
    </Box>
  );
};

const TrendBadge = ({ wow }) => {
  if (!wow) return null;
  const up = wow.pct >= 0;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.2,
        py: 0.4,
        borderRadius: "999px",
        fontSize: "0.75rem",
        fontWeight: 700,
        bgcolor: up ? `${brand.success}22` : `${brand.danger}22`,
        color: up ? "#10B981" : brand.danger,
        border: `1px solid ${up ? "#10B98144" : `${brand.danger}44`}`,
      }}
    >
      {up ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} />}
      {Math.abs(wow.pct)}%
    </Box>
  );
};

const EmptyChartState = ({ text }) => (
  <Box height="100%" minHeight={180} display="flex" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" px={2} gap={1}>
    <MdAnalytics size={32} color={brand.slateLight} />
    <Typography sx={{ color: brand.slate, fontSize: "0.85rem", fontWeight: 500 }}>{text}</Typography>
  </Box>
);

const CardShell = ({ children, sx = {} }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "22px",
      height: "100%",
      bgcolor: brand.paper,
      border: `1px solid ${brand.slateLight}40`,
      boxShadow: "0 10px 30px -15px rgba(11,10,61,0.06)",
      transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: "0 22px 45px -20px rgba(11,10,61,0.15)",
        borderColor: `${brand.brass}66`,
      },
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const StatusChip = ({ status }) => {
  const normalized = String(status || "pending").toLowerCase();
  let bg = brand.brassSoft;
  let color = brand.ink950;
  let icon = <MdSchedule size={14} />;

  if (normalized === "completed" || normalized === "delivered" || normalized === "confirmed") {
    bg = `${brand.success}1E`;
    color = brand.success;
    icon = <MdCheckCircle size={14} />;
  } else if (normalized === "cancelled" || normalized === "rejected") {
    bg = `${brand.danger}1E`;
    color = brand.danger;
    icon = <MdCancel size={14} />;
  } else if (normalized === "pending" || normalized === "processing") {
    bg = `${brand.brass}1E`;
    color = "#B4800A";
    icon = <MdPendingActions size={14} />;
  }

  return (
    <Chip
      icon={icon}
      label={normalized}
      size="small"
      sx={{
        textTransform: "capitalize",
        bgcolor: bg,
        color: color,
        fontWeight: 700,
        fontSize: "0.75rem",
        px: 0.5,
        "& .MuiChip-icon": { color: "inherit" },
      }}
    />
  );
};

const SummaryPill = ({ icon, label, value, tone = "neutral" }) => {
  const toneMap = {
    neutral: { bg: `${brand.slateLight}25`, color: brand.ink950 },
    danger: { bg: `${brand.danger}1E`, color: brand.danger },
    success: { bg: `${brand.success}1E`, color: brand.success },
  };
  const { bg, color } = toneMap[tone] || toneMap.neutral;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.1,
        px: 1.6,
        py: 1,
        borderRadius: "14px",
        bgcolor: bg,
        minWidth: 128,
      }}
    >
      <Box sx={{ color, display: "flex" }}>{icon}</Box>
      <Box>
        <Typography sx={{ fontSize: "1rem", fontWeight: 800, color, lineHeight: 1.1 }}>{value}</Typography>
        <Typography sx={{ fontSize: "0.65rem", color: brand.slate, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

const WorkBadge = ({ icon, label, value, bg, color }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.5,
      px: 1.1,
      py: 0.5,
      borderRadius: "9px",
      bgcolor: bg,
      color,
      fontSize: "0.7rem",
      fontWeight: 700,
      whiteSpace: "nowrap",
    }}
  >
    {icon}
    {value} {label}
  </Box>
);

// ── Main Dashboard ────────────────────────────────────────────────────────
const Dashboard = () => {
  const [role, setRole] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeframe, setTimeframe] = useState(14); // 7, 14, 30 days
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [raw, setRaw] = useState({
    users: [],
    orders: [],
    services: [],
    categories: [],
    appointments: [],
    employees: [],
  });

  const fetchStats = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);

    try {
      let userObj = null;
      try {
        const meRes = await getMe();
        userObj = meRes?.data?.user;
      } catch {
        const stored = localStorage.getItem("user");
        userObj = stored ? JSON.parse(stored) : null;
      }

      const userRole = (userObj?.role || localStorage.getItem("role") || "admin").toLowerCase();
      setRole(userRole);
      if (userObj) setUserInfo(userObj);

      if (userRole === "employee") {
        const appointments = await getAllAppointments();
        setRaw((prev) => ({ ...prev, appointments: Array.isArray(appointments) ? appointments : [] }));
      } else if (userRole === "vendor") {
        const [orders, services, categories] = await Promise.all([
          getAllOrders().catch(() => []),
          getAllservices().catch(() => []),
          getAllCategories().catch(() => []),
        ]);

        const myName = (userObj?.name || "").toLowerCase();
        const ordersList = Array.isArray(orders) ? orders : [];
        const vendorOrders = myName
          ? ordersList.filter((o) => String(o.assigned_person || "").toLowerCase() === myName)
          : ordersList;

        setRaw({
          users: [],
          orders: vendorOrders,
          services: Array.isArray(services) ? services : [],
          categories: Array.isArray(categories) ? categories : [],
          appointments: [],
          employees: [],
        });
      } else {
        const [users, orders, services, categories, appointments, employees] = await Promise.all([
          getAllUsers().catch(() => []),
          getAllOrders().catch(() => []),
          getAllservices().catch(() => []),
          getAllCategories().catch(() => []),
          getAllAppointments().catch(() => []),
          getAllEmployees().catch(() => []),
        ]);

        setRaw({
          users: Array.isArray(users) ? users : [],
          orders: Array.isArray(orders) ? orders : [],
          services: Array.isArray(services) ? services : [],
          categories: Array.isArray(categories) ? categories : [],
          appointments: Array.isArray(appointments) ? appointments : [],
          employees: Array.isArray(employees) ? employees : [],
        });
      }
    } catch (error) {
      console.error("Failed to load dashboard stats", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // Computed Analytics
  const appointmentTrend = useMemo(() => buildDailyTrend(raw.appointments, timeframe), [raw.appointments, timeframe]);
  const appointmentWoW = useMemo(() => weekOverWeek(raw.appointments), [raw.appointments]);
  const orderWoW = useMemo(() => weekOverWeek(raw.orders), [raw.orders]);
  const orderStatus = useMemo(() => buildBreakdown(raw.orders, getStatus), [raw.orders]);
  const serviceCategories = useMemo(() => buildBreakdown(raw.services, getCategoryName), [raw.services]);

  const orderCompletionStats = useMemo(() => {
    let completed = 0;
    raw.orders.forEach((o) => {
      const st = getStatus(o);
      if (st === "completed" || st === "delivered") completed += 1;
    });
    const total = raw.orders.length || 1;
    return Math.round((completed / total) * 100);
  }, [raw.orders]);

  const appointmentStatusStats = useMemo(() => {
    let pending = 0;
    let confirmed = 0;
    let completed = 0;
    let cancelled = 0;

    raw.appointments.forEach((apt) => {
      const st = getStatus(apt);
      if (st === "completed" || st === "delivered") completed += 1;
      else if (st === "confirmed" || st === "approved") confirmed += 1;
      else if (st === "cancelled" || st === "rejected") cancelled += 1;
      else pending += 1;
    });

    const total = raw.appointments.length || 1;
    const completionRate = Math.round(((completed + confirmed) / total) * 100);

    return { pending, confirmed, completed, cancelled, total: raw.appointments.length, completionRate };
  }, [raw.appointments]);

  const employeeWorkflow = useMemo(() => {
    const byEmployee = new Map();

    raw.employees.forEach((emp) => {
      const key = (emp.name || "").trim().toLowerCase();
      if (!key) return;
      byEmployee.set(key, {
        id: emp.id || emp._id || key,
        name: emp.name,
        role: emp.role || "employee",
        assigned: 0,
        inProgress: 0,
        completed: 0,
        total: 0,
      });
    });

    let unassigned = 0;

    raw.appointments.forEach((apt) => {
      const assignedTo = (apt.assigned_person || "").trim();
      if (!assignedTo) {
        unassigned += 1;
        return;
      }

      const key = assignedTo.toLowerCase();
      if (!byEmployee.has(key)) {
        byEmployee.set(key, {
          id: key,
          name: assignedTo,
          role: "employee",
          assigned: 0,
          inProgress: 0,
          completed: 0,
          total: 0,
        });
      }

      const entry = byEmployee.get(key);
      const workStatus = String(apt.work_status || "assigned").toLowerCase();
      entry.total += 1;
      if (workStatus === "completed") entry.completed += 1;
      else if (workStatus === "in_progress" || workStatus === "in progress") entry.inProgress += 1;
      else entry.assigned += 1;
    });

    const employeesList = Array.from(byEmployee.values()).sort((a, b) => {
      const loadA = a.assigned + a.inProgress;
      const loadB = b.assigned + b.inProgress;
      return loadB - loadA || b.total - a.total || a.name.localeCompare(b.name);
    });

    const activeWorkforce = employeesList.filter((e) => e.total > 0).length;
    const totalCompleted = employeesList.reduce((s, e) => s + e.completed, 0);
    const totalJobs = employeesList.reduce((s, e) => s + e.total, 0);
    const overallCompletionRate = totalJobs > 0 ? Math.round((totalCompleted / totalJobs) * 100) : 0;

    return { employeesList, unassigned, activeWorkforce, overallCompletionRate };
  }, [raw.appointments, raw.employees]);

  const selectedEmployeeJobs = useMemo(() => {
    if (!selectedEmployee) return [];
    const key = selectedEmployee.name.trim().toLowerCase();

    return [...raw.appointments]
      .filter((apt) => (apt.assigned_person || "").trim().toLowerCase() === key)
      .sort((a, b) => new Date(getDate(b) || 0) - new Date(getDate(a) || 0));
  }, [selectedEmployee, raw.appointments]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good Morning";
    if (h < 18) return "Good Afternoon";
    return "Good Evening";
  }, []);

  const todayDateStr = useMemo(() => {
    return new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, []);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="70vh" gap={2}>
        <CircularProgress size={48} sx={{ color: brand.brass }} />
        <Typography sx={{ color: brand.slate, fontSize: "0.95rem", fontWeight: 600 }}>
          Initializing Executive Dashboard…
        </Typography>
      </Box>
    );
  }

  const isVendor = role === "vendor";
  const isAdmin = role !== "employee" && role !== "vendor";

  return (
    <Box p={{ xs: 2, sm: 3, md: 4 }} sx={{ maxWidth: 1600, margin: "0 auto" }}>
      {/* ── Top Header & Actions ────────────────────────────────────────── */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={4}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1.5} mb={0.8}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: brand.success,
                boxShadow: `0 0 12px ${brand.success}`,
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
              }}
            />
            <Typography sx={{ color: brand.slate, fontSize: "0.85rem", fontWeight: 600, letterSpacing: "0.02em" }}>
              {todayDateStr}
            </Typography>
            <Chip
              icon={<MdVerifiedUser size={13} />}
              label={isAdmin ? "ADMINISTRATOR" : isVendor ? "VENDOR PARTNER" : "STAFF MEMBER"}
              size="small"
              sx={{
                bgcolor: isAdmin ? brand.ink950 : isVendor ? "#8B5CF622" : brand.brassSoft,
                color: isAdmin ? brand.parchment : isVendor ? "#6D28D9" : brand.ink950,
                fontWeight: 700,
                fontSize: "0.68rem",
                height: 22,
                "& .MuiChip-icon": { color: "inherit" },
              }}
            />
          </Box>
          <Typography
            variant="h3"
            sx={{
              color: brand.ink950,
              fontFamily: '"Fraunces", serif',
              fontWeight: 700,
              fontSize: { xs: "1.8rem", md: "2.4rem" },
              letterSpacing: "-0.02em",
            }}
          >
            {greeting}, {userInfo?.name || (isAdmin ? "Admin" : "Employee")}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
          {/* Timeframe Selector */}
          <Box
            sx={{
              display: "flex",
              bgcolor: `${brand.slateLight}30`,
              p: 0.5,
              borderRadius: "14px",
              border: `1px solid ${brand.slateLight}60`,
            }}
          >
            {[
              { label: "7D", val: 7 },
              { label: "14D", val: 14 },
              { label: "30D", val: 30 },
            ].map((tf) => (
              <Button
                key={tf.val}
                size="small"
                onClick={() => setTimeframe(tf.val)}
                sx={{
                  px: 2,
                  py: 0.6,
                  borderRadius: "10px",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  minWidth: 46,
                  color: timeframe === tf.val ? brand.ink950 : brand.slate,
                  bgcolor: timeframe === tf.val ? brand.paper : "transparent",
                  boxShadow: timeframe === tf.val ? "0 4px 12px rgba(11,10,61,0.08)" : "none",
                  "&:hover": { bgcolor: timeframe === tf.val ? brand.paper : `${brand.brass}1E` },
                }}
              >
                {tf.label}
              </Button>
            ))}
          </Box>

          {/* Refresh Button */}
          <MuiTooltip title="Refresh Dashboard Analytics">
            <IconButton
              onClick={() => fetchStats(true)}
              disabled={refreshing}
              sx={{
                bgcolor: brand.paper,
                border: `1px solid ${brand.slateLight}60`,
                p: 1.1,
                borderRadius: "14px",
                color: brand.ink950,
                boxShadow: "0 4px 12px rgba(11,10,61,0.05)",
                "&:hover": { bgcolor: brand.brassSoft, borderColor: brand.brass },
              }}
            >
              <MdRefresh size={20} className={refreshing ? "animate-spin" : ""} />
            </IconButton>
          </MuiTooltip>
        </Box>
      </Box>

      {/* ── KPI Grid Section ───────────────────────────────────────────── */}
      <Grid container spacing={3} mb={4}>
        {/* Hero Card: Appointment Overview */}
        <Grid item xs={12} lg={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3.5,
              borderRadius: "24px",
              height: "100%",
              minHeight: 250,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              color: brand.parchment,
              background: `radial-gradient(130% 140% at 100% 0%, ${brand.ink700} 0%, ${brand.ink950} 65%)`,
              boxShadow: "0 22px 50px -15px rgba(11,10,61,0.5)",
              position: "relative",
              overflow: "hidden",
              border: `1px solid ${brand.brass}33`,
            }}
          >
            {/* Glowing Accent Orb */}
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 190,
                height: 190,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${brand.brass} 0%, transparent 70%)`,
                opacity: 0.35,
                filter: "blur(32px)",
              }}
            />

            <Box display="flex" alignItems="center" justifyContent="space-between" position="relative" zIndex={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    bgcolor: `${brand.brass}22`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: brand.brassLight,
                  }}
                >
                  {isVendor ? <MdOutlineShoppingBag size={20} /> : <MdEventNote size={20} />}
                </Box>
                <Typography sx={{ opacity: 0.85, fontSize: "0.88rem", fontWeight: 600 }}>
                  {isVendor ? "Total Orders" : "Total Appointments"}
                </Typography>
              </Box>
              <TrendBadge wow={isVendor ? orderWoW : appointmentWoW} />
            </Box>

            <Box position="relative" zIndex={1} my={2}>
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: { xs: "3rem", sm: "3.6rem" },
                  lineHeight: 1,
                  color: "#FFFFFF",
                  letterSpacing: "-0.03em",
                }}
              >
                {isVendor ? raw.orders.length : raw.appointments.length}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", opacity: 0.75, mt: 1 }}>
                {isVendor ? "Recorded order fulfillments" : "Recorded booking engagements"}
              </Typography>
            </Box>

            {/* Quick Metrics Progress Bar */}
            <Box position="relative" zIndex={1}>
              <Box display="flex" justifyContent="space-between" mb={0.8}>
                <Typography sx={{ fontSize: "0.72rem", opacity: 0.8, fontWeight: 600 }}>
                  {isVendor ? "Fulfillment Velocity" : "Completion Velocity"}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: brand.brassLight }}>
                  {isVendor ? orderCompletionStats : appointmentStatusStats.completionRate}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={isVendor ? orderCompletionStats : appointmentStatusStats.completionRate}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: "rgba(255,255,255,0.12)",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    background: `linear-gradient(90deg, ${brand.brass} 0%, ${brand.brassLight} 100%)`,
                  },
                }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Secondary KPI Cards */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={2.5} height="100%">
            {(isVendor
              ? [
                  {
                    label: "Active Services",
                    count: raw.services.length,
                    icon: <MdOutlineDesignServices size={22} />,
                    accent: brand.brass,
                    subtext: "Service catalog offerings",
                  },
                  {
                    label: "Service Categories",
                    count: raw.categories.length,
                    icon: <MdOutlineCategory size={22} />,
                    accent: "#8B5CF6",
                    subtext: "Catalog classifications",
                  },
                ]
              : [
                  {
                    label: "Registered Users",
                    count: raw.users.length,
                    icon: <MdPeople size={22} />,
                    accent: "#3B82F6",
                    subtext: "System users & clients",
                  },
                  {
                    label: "Total Orders",
                    count: raw.orders.length,
                    icon: <MdOutlineShoppingBag size={22} />,
                    accent: "#10B981",
                    subtext: "Placed transactions",
                  },
                  {
                    label: "Active Services",
                    count: raw.services.length,
                    icon: <MdOutlineDesignServices size={22} />,
                    accent: brand.brass,
                    subtext: "Service catalog offerings",
                  },
                  {
                    label: "Service Categories",
                    count: raw.categories.length,
                    icon: <MdOutlineCategory size={22} />,
                    accent: "#8B5CF6",
                    subtext: "Catalog classifications",
                  },
                ]
            ).map((kpi) => (
              <Grid item xs={12} sm={isVendor ? 6 : 6} key={kpi.label}>
                <CardShell
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minHeight: 115,
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Box
                        sx={{
                          width: 42,
                          height: 42,
                          borderRadius: "14px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: `${kpi.accent}14`,
                          color: kpi.accent,
                        }}
                      >
                        {kpi.icon}
                      </Box>
                      <Typography sx={{ color: brand.slate, fontSize: "0.85rem", fontWeight: 600 }}>
                        {kpi.label}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="baseline" justifyContent="space-between" mt={1.5}>
                    <Typography
                      sx={{
                        fontFamily: '"Fraunces", serif',
                        fontWeight: 700,
                        fontSize: "2.2rem",
                        color: brand.ink950,
                        lineHeight: 1,
                      }}
                    >
                      {isAdmin || kpi.label === "Active Services" || kpi.label === "Service Categories" ? kpi.count : "—"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: brand.slate, fontWeight: 500 }}>
                      {kpi.subtext}
                    </Typography>
                  </Box>
                </CardShell>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* ── Main Charts Section (Appointments) ──────────────────────────── */}
      {!isVendor && (
        <Grid container spacing={3} mb={4}>
          {/* Trend Area Chart */}
          <Grid item xs={12} lg={8}>
          <CardShell sx={{ minHeight: 360 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={3}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: '"Fraunces", serif',
                    fontWeight: 700,
                    fontSize: "1.25rem",
                    color: brand.ink950,
                  }}
                >
                  Appointment Analytics Trend
                </Typography>
                <Typography sx={{ color: brand.slate, fontSize: "0.78rem" }}>
                  Daily volume over the past {timeframe} days
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Box display="flex" alignItems="center" gap={0.8}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: brand.brass }} />
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 600 }}>Total</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.8}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: brand.ink950 }} />
                  <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 600 }}>Completed</Typography>
                </Box>
              </Box>
            </Box>

            {raw.appointments.length === 0 ? (
              <EmptyChartState text="No appointment records found for the selected timeframe." />
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={appointmentTrend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="brassGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={brand.brass} stopOpacity={0.4} />
                      <stop offset="95%" stopColor={brand.brass} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="navyGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={brand.ink950} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={brand.ink950} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={`${brand.slateLight}40`} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: brand.slate, fontWeight: 500 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: brand.slate, fontWeight: 500 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    name="Total Appointments"
                    stroke={brand.brass}
                    strokeWidth={3}
                    fill="url(#brassGradient)"
                  />
                  <Area
                    type="monotone"
                    dataKey="completed"
                    name="Completed"
                    stroke={brand.ink950}
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    fill="url(#navyGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardShell>
        </Grid>

        {/* Status Breakdown Donut Chart */}
        <Grid item xs={12} lg={4}>
          <CardShell sx={{ minHeight: 360, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            <Box mb={1}>
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  color: brand.ink950,
                }}
              >
                Status Distribution
              </Typography>
              <Typography sx={{ color: brand.slate, fontSize: "0.78rem" }}>
                Current appointment status metrics
              </Typography>
            </Box>

            {raw.appointments.length === 0 ? (
              <EmptyChartState text="No appointment breakdown data available." />
            ) : (
              <Box position="relative" display="flex" flexDirection="column" alignItems="center" my={1}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: "Pending", value: appointmentStatusStats.pending },
                        { name: "Confirmed", value: appointmentStatusStats.confirmed },
                        { name: "Completed", value: appointmentStatusStats.completed },
                        { name: "Cancelled", value: appointmentStatusStats.cancelled },
                      ].filter((d) => d.value > 0)}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={65}
                      outerRadius={90}
                      paddingAngle={3}
                      stroke="none"
                    >
                      <Cell fill="#F59E0B" />
                      <Cell fill="#3B82F6" />
                      <Cell fill="#10B981" />
                      <Cell fill={brand.danger} />
                    </Pie>
                    <Tooltip content={<ChartTooltip />} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Donut Center Stat */}
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -60%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Fraunces", serif',
                      fontWeight: 700,
                      fontSize: "1.8rem",
                      color: brand.ink950,
                      lineHeight: 1,
                    }}
                  >
                    {raw.appointments.length}
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: brand.slate, fontWeight: 600, mt: 0.3 }}>
                    Total Bookings
                  </Typography>
                </Box>

                {/* Custom Legend */}
                <Grid container spacing={1} mt={1}>
                  {[
                    { label: "Pending", val: appointmentStatusStats.pending, color: "#F59E0B" },
                    { label: "Confirmed", val: appointmentStatusStats.confirmed, color: "#3B82F6" },
                    { label: "Completed", val: appointmentStatusStats.completed, color: "#10B981" },
                    { label: "Cancelled", val: appointmentStatusStats.cancelled, color: brand.danger },
                  ].map((item) => (
                    <Grid item xs={6} key={item.label}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={0.8}
                        sx={{ bgcolor: `${brand.slateLight}1A`, borderRadius: "10px" }}
                      >
                        <Box display="flex" alignItems="center" gap={0.8}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color }} />
                          <Typography sx={{ fontSize: "0.72rem", color: brand.ink950, fontWeight: 600 }}>
                            {item.label}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: brand.ink950 }}>
                          {item.val}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </CardShell>
        </Grid>
      </Grid>
      )}

      {/* ── Secondary Charts Section (Orders & Categories) ────────────────── */}
      {(isAdmin || isVendor) && (
        <Grid container spacing={3} mb={4}>
          {/* Orders by Status */}
          <Grid item xs={12} md={6}>
            <CardShell sx={{ minHeight: 300 }}>
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: "1.15rem",
                  color: brand.ink950,
                  mb: 2,
                }}
              >
                Orders Breakdown by Status
              </Typography>
              {orderStatus.total === 0 ? (
                <EmptyChartState text="No order history available." />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={orderStatus.data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={`${brand.slateLight}40`} vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: brand.slate }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: brand.slate }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="value" name="Orders" fill={brand.ink950} radius={[6, 6, 0, 0]} barSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardShell>
          </Grid>

          {/* Top Service Categories */}
          <Grid item xs={12} md={6}>
            <CardShell sx={{ minHeight: 300 }}>
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: "1.15rem",
                  color: brand.ink950,
                  mb: 2,
                }}
              >
                Service Category Distribution
              </Typography>
              {serviceCategories.total === 0 ? (
                <EmptyChartState text="No services catalog records found." />
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={serviceCategories.data.slice(0, 6)} layout="vertical" margin={{ top: 0, right: 10, left: 20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={`${brand.slateLight}40`} horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: brand.slate }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: brand.ink950, fontWeight: 600 }} axisLine={false} tickLine={false} width={100} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="value" name="Services" fill={brand.brass} radius={[0, 6, 6, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardShell>
          </Grid>
        </Grid>
      )}

      {/* ── Employee Workflow ─────────────────────────────────────────── */}
      {!isVendor && (
        <CardShell>
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                bgcolor: brand.brassSoft,
                color: brand.ink950,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdAssignmentInd size={22} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontFamily: '"Fraunces", serif',
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  color: brand.ink950,
                }}
              >
                Employee Workflow
              </Typography>
              <Typography sx={{ color: brand.slate, fontSize: "0.78rem" }}>
                Live workload & job pipeline for field staff
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
            <SummaryPill
              icon={<MdErrorOutline size={20} />}
              label="Needs Assignment"
              value={employeeWorkflow.unassigned}
              tone={employeeWorkflow.unassigned > 0 ? "danger" : "success"}
            />
            <SummaryPill
              icon={<MdPeople size={20} />}
              label="Active Staff"
              value={employeeWorkflow.activeWorkforce}
            />
            <SummaryPill
              icon={<MdDoneAll size={20} />}
              label="Completion Rate"
              value={`${employeeWorkflow.overallCompletionRate}%`}
              tone="success"
            />
          </Box>
        </Box>

        {employeeWorkflow.employeesList.length === 0 ? (
          <EmptyChartState text="No field staff activity yet. Assign a staff member from Appointments to populate this workflow." />
        ) : (
          <Box display="flex" flexDirection="column" gap={1.25}>
            {employeeWorkflow.employeesList.slice(0, 8).map((emp) => {
              const activeLoad = emp.assigned + emp.inProgress;
              const completionRate = emp.total > 0 ? Math.round((emp.completed / emp.total) * 100) : 0;

              return (
                <Box
                  key={emp.id}
                  onClick={() => setSelectedEmployee(emp)}
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                  gap={2}
                  p={2}
                  sx={{
                    borderRadius: "14px",
                    border: `1px solid ${brand.slateLight}35`,
                    cursor: "pointer",
                    transition: "background-color 150ms ease, border-color 150ms ease",
                    "&:hover": { bgcolor: `${brand.brassSoft}55`, borderColor: `${brand.brass}66` },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2} minWidth={0} flex={1}>
                    <Avatar
                      sx={{
                        bgcolor: brand.ink950,
                        color: brand.brassLight,
                        fontWeight: 700,
                        width: 42,
                        height: 42,
                        fontSize: "0.95rem",
                      }}
                    >
                      {emp.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box minWidth={0}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: brand.ink950 }} noWrap>
                        {emp.name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.75rem", color: brand.slate, textTransform: "capitalize" }} noWrap>
                        {emp.role} • {emp.total} job{emp.total === 1 ? "" : "s"} total
                        {activeLoad > 0 ? ` • ${activeLoad} active` : ""}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.75}>
                    <WorkBadge
                      icon={<MdPendingActions size={13} />}
                      label="Assigned"
                      value={emp.assigned}
                      bg={`${brand.brass}20`}
                      color="#B4800A"
                    />
                    <WorkBadge
                      icon={<MdLocalShipping size={13} />}
                      label="In Progress"
                      value={emp.inProgress}
                      bg="#3B82F61E"
                      color="#3B82F6"
                    />
                    <WorkBadge
                      icon={<MdCheckCircle size={13} />}
                      label="Done"
                      value={emp.completed}
                      bg={`${brand.success}1E`}
                      color={brand.success}
                    />
                  </Box>

                  <Box minWidth={{ xs: "100%", md: 140 }}>
                    <Box display="flex" justifyContent="space-between" mb={0.4}>
                      <Typography sx={{ fontSize: "0.68rem", color: brand.slate, fontWeight: 700 }}>
                        COMPLETION
                      </Typography>
                      <Typography sx={{ fontSize: "0.68rem", color: brand.ink950, fontWeight: 700 }}>
                        {completionRate}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={completionRate}
                      sx={{
                        height: 6,
                        borderRadius: "999px",
                        bgcolor: `${brand.slateLight}30`,
                        "& .MuiLinearProgress-bar": {
                          borderRadius: "999px",
                          background: `linear-gradient(90deg, ${brand.brass} 0%, ${brand.success} 100%)`,
                        },
                      }}
                    />
                  </Box>

                  <MuiTooltip title="View work photos & job details">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEmployee(emp);
                      }}
                      sx={{
                        bgcolor: `${brand.ink950}0D`,
                        color: brand.ink950,
                        "&:hover": { bgcolor: brand.ink950, color: brand.brassLight },
                      }}
                    >
                      <MdOutlinePhotoCamera size={18} />
                    </IconButton>
                  </MuiTooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </CardShell>
      )}

      {/* ── Employee Work Detail Dialog ───────────────────────────────── */}
      <Dialog
        open={Boolean(selectedEmployee)}
        onClose={() => setSelectedEmployee(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", bgcolor: brand.paper } }}
      >
        {selectedEmployee && (
          <>
            <DialogTitle sx={{ p: 0 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  p: 3,
                  background: `radial-gradient(130% 140% at 100% 0%, ${brand.ink700} 0%, ${brand.ink950} 65%)`,
                  color: brand.parchment,
                }}
              >
                <Box display="flex" alignItems="center" gap={2} minWidth={0}>
                  <Avatar
                    sx={{
                      bgcolor: `${brand.brass}22`,
                      color: brand.brassLight,
                      fontWeight: 700,
                      width: 48,
                      height: 48,
                      border: `1px solid ${brand.brass}44`,
                    }}
                  >
                    {selectedEmployee.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box minWidth={0}>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", fontFamily: '"Fraunces", serif' }} noWrap>
                      {selectedEmployee.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: brand.brassLight, textTransform: "capitalize" }}>
                      {selectedEmployee.role} • {selectedEmployeeJobs.length} job{selectedEmployeeJobs.length === 1 ? "" : "s"} on record
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setSelectedEmployee(null)} sx={{ color: brand.parchment }}>
                  <MdClose size={20} />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
              <Box display="flex" flexWrap="wrap" gap={1.5} mb={3}>
                <WorkBadge
                  icon={<MdPendingActions size={13} />}
                  label="Assigned"
                  value={selectedEmployee.assigned}
                  bg={`${brand.brass}20`}
                  color="#B4800A"
                />
                <WorkBadge
                  icon={<MdLocalShipping size={13} />}
                  label="In Progress"
                  value={selectedEmployee.inProgress}
                  bg="#3B82F61E"
                  color="#3B82F6"
                />
                <WorkBadge
                  icon={<MdCheckCircle size={13} />}
                  label="Completed"
                  value={selectedEmployee.completed}
                  bg={`${brand.success}1E`}
                  color={brand.success}
                />
              </Box>

              {selectedEmployeeJobs.length === 0 ? (
                <EmptyChartState text="No jobs recorded for this employee yet." />
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  {selectedEmployeeJobs.map((apt, i) => {
                    const workStatus = String(apt.work_status || "assigned").toLowerCase();
                    const dateVal = getDate(apt);
                    const name = getCustomerName(apt);
                    const service = getServiceName(apt);
                    const phone = apt?.phone || apt?.customer_phone || "No contact info";
                    const hasProof = apt.start_image || apt.end_image;

                    return (
                      <Box
                        key={apt.id || apt._id || i}
                        sx={{
                          borderRadius: "16px",
                          border: `1px solid ${brand.slateLight}40`,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection={{ xs: "column", sm: "row" }}
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          justifyContent="space-between"
                          gap={1.5}
                          p={2}
                          sx={{ bgcolor: `${brand.slateLight}15` }}
                        >
                          <Box minWidth={0}>
                            <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", color: brand.ink950 }} noWrap>
                              {name}
                            </Typography>
                            <Typography sx={{ fontSize: "0.78rem", color: brand.slate }} noWrap>
                              {service} • <span style={{ fontFamily: "monospace" }}>{phone}</span>
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <MdOutlineCalendarToday size={13} color={brand.slate} />
                              <Typography sx={{ fontSize: "0.75rem", color: brand.slate, fontWeight: 600 }}>
                                {dateVal && !isNaN(new Date(dateVal))
                                  ? new Date(dateVal).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                                  : "Date Pending"}
                              </Typography>
                            </Box>
                            <StatusChip status={getStatus(apt)} />
                          </Box>
                        </Box>

                        <Box p={2}>
                          {apt.location && (
                            <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
                              <MdLocationOn size={14} color={brand.slate} />
                              <Typography sx={{ fontSize: "0.78rem", color: brand.slate }}>{apt.location}</Typography>
                            </Box>
                          )}

                          {!hasProof ? (
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              p={1.5}
                              sx={{ bgcolor: `${brand.slateLight}20`, borderRadius: "10px" }}
                            >
                              <MdOutlinePhotoCamera size={16} color={brand.slate} />
                              <Typography sx={{ fontSize: "0.78rem", color: brand.slate }}>
                                No work proof photos uploaded yet for this job
                                {workStatus !== "completed" ? " — staff app will capture them on start/completion" : ""}.
                              </Typography>
                            </Box>
                          ) : (
                            <Grid container spacing={1.5}>
                              <Grid item xs={12} sm={6}>
                                <Box bgcolor="#FFFFFF" p={1.5} borderRadius="12px" border="1px solid #E2E8F0">
                                  <Chip
                                    label="Start Work Proof"
                                    size="small"
                                    sx={{ bgcolor: "#E0F2FE", color: "#0369A1", fontWeight: 700, fontSize: "0.68rem", mb: 1 }}
                                  />
                                  {apt.start_time && (
                                    <Typography sx={{ fontSize: "0.7rem", color: brand.slate, mb: 1 }}>
                                      {new Date(apt.start_time).toLocaleString()}
                                    </Typography>
                                  )}
                                  {apt.start_image ? (
                                    <Box
                                      component="img"
                                      src={getImageUrl(apt.start_image)}
                                      alt="Start work proof"
                                      sx={{ width: "100%", height: 140, objectFit: "cover", borderRadius: "8px", border: "1px solid #CBD5E1" }}
                                    />
                                  ) : (
                                    <Box height={100} display="flex" alignItems="center" justifyContent="center" bgcolor="#F1F5F9" borderRadius="8px">
                                      <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8" }}>No Start Photo</Typography>
                                    </Box>
                                  )}
                                  {apt.start_latitude && apt.start_longitude && (
                                    <Button
                                      size="small"
                                      component="a"
                                      href={`https://maps.google.com/?q=${apt.start_latitude},${apt.start_longitude}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      endIcon={<MdLaunch size={12} />}
                                      sx={{ p: 0, mt: 1, fontSize: "0.7rem", textTransform: "none", fontWeight: 700 }}
                                    >
                                      Open in Google Maps
                                    </Button>
                                  )}
                                </Box>
                              </Grid>

                              <Grid item xs={12} sm={6}>
                                <Box bgcolor="#FFFFFF" p={1.5} borderRadius="12px" border="1px solid #E2E8F0">
                                  <Chip
                                    label="Complete Work Proof"
                                    size="small"
                                    sx={{ bgcolor: "#DCFCE7", color: "#15803D", fontWeight: 700, fontSize: "0.68rem", mb: 1 }}
                                  />
                                  {apt.end_time && (
                                    <Typography sx={{ fontSize: "0.7rem", color: brand.slate, mb: 1 }}>
                                      {new Date(apt.end_time).toLocaleString()}
                                    </Typography>
                                  )}
                                  {apt.end_image ? (
                                    <Box
                                      component="img"
                                      src={getImageUrl(apt.end_image)}
                                      alt="Completed work proof"
                                      sx={{ width: "100%", height: 140, objectFit: "cover", borderRadius: "8px", border: "1px solid #CBD5E1" }}
                                    />
                                  ) : (
                                    <Box height={100} display="flex" alignItems="center" justifyContent="center" bgcolor="#F1F5F9" borderRadius="8px">
                                      <Typography sx={{ fontSize: "0.72rem", color: "#94A3B8" }}>No Complete Photo</Typography>
                                    </Box>
                                  )}
                                  {apt.end_latitude && apt.end_longitude && (
                                    <Button
                                      size="small"
                                      component="a"
                                      href={`https://maps.google.com/?q=${apt.end_latitude},${apt.end_longitude}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      endIcon={<MdLaunch size={12} />}
                                      sx={{ p: 0, mt: 1, fontSize: "0.7rem", textTransform: "none", fontWeight: 700 }}
                                    >
                                      Open in Google Maps
                                    </Button>
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          )}

                          {apt.comment && (
                            <Box mt={1.5} bgcolor={`${brand.slateLight}20`} p={1.5} borderRadius="10px">
                              <Typography sx={{ fontSize: "0.78rem", color: brand.ink950 }}>{apt.comment}</Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Dashboard;